import { supabase } from './supabase'

// Keywords in a bid's job_type / bid_type / line descriptions that mean the
// job is a NEW above-ground pool install and DOES need a sand note.
const INSTALL_KEYWORDS = [
  'above_ground',
  'above ground',
  'aboveground',
  'new install',
  'ag install',
  'pool install',
]

// Keywords that indicate the job is NOT an above-ground install and should
// never get a sand note, regardless of other hints.
const NO_SAND_KEYWORDS = [
  'liner',
  'opening',
  'closing',
  'close',
  'open',
  'service',
  'repair',
  'in-ground',
  'inground',
  'in_ground',
  'equipment',
  'heater',
  'pump',
  'filter only',
]

function includesAny(haystack, needles) {
  if (!haystack) return false
  const h = String(haystack).toLowerCase()
  return needles.some(n => h.includes(n))
}

// From a bid and its line items, decide whether sand is required.
// Returns one of: 'above_ground_install' | 'no_sand' | 'unknown'.
// 'unknown' means it's a pool job but we can't tell — default to not required
// so the owner must opt-in via the confirmation modal.
export function detectInstallType(bid, bidLines) {
  if (!bid) return 'no_sand'
  if (bid.business_id !== 'pools') return 'no_sand'
  const jobType = bid.bid_type || bid.job_type || ''
  const lineText = (bidLines || []).map(l => l?.description || '').join(' ')
  const combined = jobType + ' ' + lineText
  if (includesAny(combined, NO_SAND_KEYWORDS)) return 'no_sand'
  if (includesAny(combined, INSTALL_KEYWORDS)) return 'above_ground_install'
  return 'unknown'
}

// Parse a pool size from free-form text, matching the seeded standards.
// Returns the canonical pool_size string, or null.
// Handles "18 round", "18'", "18ft round", "15x30", "18 x 33 oval", etc.
export function parsePoolSize(text) {
  if (!text) return null
  const raw = String(text).toLowerCase()

  // Oval first (e.g. "15x30", "15 x 30", "12x24 oval")
  const ovalMatch = raw.match(/(\d{2})\s*[x×]\s*(\d{2,3})/)
  if (ovalMatch) {
    const a = ovalMatch[1]
    const b = ovalMatch[2]
    return `${a}x${b} Oval`
  }

  // Round (e.g. "18'", "18 round", "21 ft round", "30ft round")
  const roundMatch = raw.match(/(\d{2})\s*(?:'|ft|foot|feet)?\s*(?:round|rd)/)
  if (roundMatch) {
    return `${roundMatch[1]}' Round`
  }

  // Bare size with explicit round hint elsewhere
  const bareMatch = raw.match(/\b(\d{2})\s*(?:'|ft)/)
  if (bareMatch && raw.includes('round')) return `${bareMatch[1]}' Round`

  return null
}

// Load all sand standards. Returns an array sorted by shape then yards.
export async function loadSandStandards() {
  const { data } = await supabase.from('pool_sand_standards').select('*').order('pool_shape').order('new_install_yards')
  return data || []
}

// Given a pool_size string + standards list, return the matching standard or null.
export function findStandard(standards, poolSize) {
  if (!poolSize) return null
  return (standards || []).find(s => s.pool_size?.toLowerCase() === String(poolSize).toLowerCase()) || null
}

// Resolve sand fields for a newly-scheduled pool job. Safe to call with any bid.
// Returns { sand_required, sand_yards, sand_type, pool_size, is_replacement, installer_provides_sand }.
export async function resolveSandForBid(bid, bidLines) {
  const base = {
    sand_required: false,
    sand_yards: null,
    sand_type: 'mason sand',
    pool_size: null,
    is_replacement: false,
    installer_provides_sand: false,
  }
  const installType = detectInstallType(bid, bidLines)
  if (installType !== 'above_ground_install') return base

  const standards = await loadSandStandards()
  const poolSize = bid.pool_size || bid.pool_dimensions || parsePoolSize((bid.bid_type || '') + ' ' + ((bidLines || []).map(l => l?.description || '').join(' ')))
  const std = findStandard(standards, poolSize)
  return {
    sand_required: true,
    sand_yards: std ? Number(std.new_install_yards) : null,
    sand_type: std?.sand_type || 'mason sand',
    pool_size: std?.pool_size || poolSize || null,
    is_replacement: false,
    installer_provides_sand: false,
  }
}

// Build the sand sentence(s) that go where {sand_section} appears in the template.
// Returns '' (empty string) if no sand note should appear.
export function buildSandSection(sand) {
  if (!sand || !sand.sand_required || sand.installer_provides_sand) return ''
  const yards = sand.sand_yards
  const type = sand.sand_type || 'mason sand'
  const yardsLabel = yards ? (Number(yards) + ' yards') : 'the required amount'
  let line = `\n\nPlease make sure to have ${yardsLabel} of ${type} delivered to the site prior to installation.`
  if (sand.is_replacement) {
    line += " (You'll need less than a full install since we're replacing your existing pool in the same spot.)"
  }
  if (sand.sand_notes) {
    line += `\n${sand.sand_notes}`
  }
  return line
}

// Replace a {sand_section} placeholder in a template body; if the template
// pre-dates the placeholder, splice the sand line in after the first paragraph.
export function applySandSection(template, sand) {
  const section = buildSandSection(sand)
  if (!template) return section
  if (template.includes('{sand_section}')) return template.replace('{sand_section}', section)
  // Legacy: no placeholder — append the sand line before the closing pleasantries.
  if (!section) return template
  const weatherIdx = template.indexOf('Because weather')
  if (weatherIdx > 0) return template.slice(0, weatherIdx) + section.trim() + '\n\n' + template.slice(weatherIdx)
  return template + section
}

// Human-readable sand summary for job card / crew view.
export function summarizeSand(job) {
  if (!job) return ''
  if (!job.sand_required) return 'No sand needed'
  const yards = job.sand_yards ? Number(job.sand_yards) + ' yards' : 'TBD'
  const type = job.sand_type || 'mason sand'
  if (job.installer_provides_sand) return `We're hauling ${yards} of ${type}`
  const suffix = job.is_replacement ? ' (REPLACEMENT — half amount)' : ''
  return `${yards} ${type} — customer providing${suffix}`
}
